import {
  DataStatus,
  DropdownItem,
  FormControl,
  FormField,
  Label,
  openDropdown,
  showNotification,
} from 'platform/components';
import {HStack, Link, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {UseFormReturn} from 'react-hook-form';

import {always, isNil, isNotNil} from 'ramda';

import {
  ContextTemplateResponseBody,
  TemplateContextEnum,
  useGetContextTemplateListQuery,
  useRenderBusinessCaseEmailTemplateMutation,
  useRenderCustomerEmailTemplateMutation,
  useRenderRentalOrderEmailTemplateMutation,
  useRenderVehicleEmailTemplateMutation,
} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {myProfileRoutes} from '@dms/routes';

import {Nullish} from 'shared/web';

import {handleApiError} from '../../../utils/handleApiError';
import {replaceVariables, VARIABLE_REGEX} from '../../../utils/replaceVariables';
import {FormValues} from '../types/SendCustomerEmailFormSchema';

interface MessageFormFieldProps {
  formApi: UseFormReturn<FormValues>;
  control: FormControl<FormValues>;
  resourceId: EntityResourceIds | Nullish;
  recordId: string | Nullish;
}

export function MessageFormField(props: MessageFormFieldProps) {
  const context = match<EntityResourceIds | Nullish, TemplateContextEnum | null>(props.resourceId)
    .with(EntityResourceIds.businessCase, always('business-case'))
    .with(EntityResourceIds.customer, always('customer'))
    .with(EntityResourceIds.serviceOrder, always('service-order'))
    .with(EntityResourceIds.rentalOrder, always('rental-order'))
    .with(EntityResourceIds.vehicle, always('vehicle'))
    .otherwise(always(null));

  const [renderCustomerTemplate, {isLoading: isRenderCustomerTemplateInProgress}] =
    useRenderCustomerEmailTemplateMutation();
  const [renderBusinessCaseTemplate, {isLoading: isRenderBusinessCaseTemplateInProgress}] =
    useRenderBusinessCaseEmailTemplateMutation();
  const [renderRentalOrderTemplate, {isLoading: isRenderRentalOrderTemplateInProgress}] =
    useRenderRentalOrderEmailTemplateMutation();
  const [renderVehicleTemplate, {isLoading: isRenderVehicleTemplateInProgress}] =
    useRenderVehicleEmailTemplateMutation();

  const isRenderTemplateInProgress =
    isRenderCustomerTemplateInProgress ||
    isRenderBusinessCaseTemplateInProgress ||
    isRenderRentalOrderTemplateInProgress ||
    isRenderVehicleTemplateInProgress;

  const hasContext = isNotNil(context);

  const {
    data: templates,
    isLoading: isLoadingTemplates,
    isError: isTemplatesError,
  } = useGetContextTemplateListQuery(
    {
      type: 'email',
      context: context ?? 'customer',
    },
    {skip: !hasContext}
  );

  const getRenderer = (template: ContextTemplateResponseBody) => {
    if (isNil(props.resourceId) || isNil(props.recordId)) {
      return;
    }

    if (props.resourceId === EntityResourceIds.rentalOrder) {
      return renderRentalOrderTemplate({
        body: {
          rentalOrderId: props.recordId,
          template: {
            subject: template.subject ?? '',
            body: template.body,
          },
        },
      });
    }

    if (props.resourceId === EntityResourceIds.vehicle) {
      return renderVehicleTemplate({
        body: {
          vehicleId: props.recordId,
          template: {
            subject: template.subject ?? '',
            body: template.body,
          },
        },
      });
    }

    if (props.resourceId === EntityResourceIds.businessCase) {
      return renderBusinessCaseTemplate({
        renderBusinessCaseEmailTemplateRequestBody: {
          template: {
            subject: template.subject ?? '',
            body: template.body,
          },
          businessCaseId: props.recordId,
        },
      });
    }

    if (props.resourceId === EntityResourceIds.customer) {
      return renderCustomerTemplate({
        renderCustomerEmailTemplateRequestBody: {
          template: {
            subject: template.subject ?? '',
            body: template.body,
          },
          customerId: props.recordId,
        },
      });
    }
  };

  const onTemplateClick = (template: ContextTemplateResponseBody) => {
    const renderer = getRenderer(template);

    if (!renderer) {
      return;
    }

    return renderer
      .unwrap()
      .then((response) => {
        props.formApi.setValue('subject', replaceVariables(response.subject));
        props.formApi.setValue('body', replaceVariables(response.body));
        if (VARIABLE_REGEX.test(response.subject) || VARIABLE_REGEX.test(response.body)) {
          showNotification.warning(i18n.t('general.notifications.notAllPlaceholdersFilled'));
        }
      })
      .catch(handleApiError);
  };

  return (
    <VStack spacing={1}>
      <FormField
        control={props.control}
        name="body"
        type="textarea"
        label={i18n.t('general.labels.message')}
        labelActions={
          hasContext
            ? [
                {
                  title: i18n.t('entity.customer.labels.chooseEmailTemplate'),
                  leftIcon: 'action/autorenew',
                  isDisabled: isRenderTemplateInProgress,
                  onClick: (event) =>
                    openDropdown(
                      event,
                      <>
                        <DataStatus isError={isTemplatesError} isLoading={isLoadingTemplates} />
                        {templates?.map((template) => (
                          <DropdownItem
                            label={template.name}
                            key={template.id}
                            onClick={() => onTemplateClick(template)}
                          />
                        ))}
                      </>,
                      {
                        isHeightLimited: true,
                      }
                    ),
                },
              ]
            : []
        }
        isRequired
        isScrollable
        rows={7}
      />
      <HStack justify="space-between" width="100%">
        <Label>{i18n.t('entity.customer.labels.messageWithSignature')}</Label>
        <Link
          leftIcon="image/edit"
          size="xSmall"
          href={myProfileRoutes.emailSignature}
          target="_blank"
        >
          {i18n.t('entity.customer.labels.goToUserSignature')}
        </Link>
      </HStack>
    </VStack>
  );
}
